'use client';

import { Link } from 'react-router-dom';
import { subscribeNewsletter } from '@/lib/clientApi';
import { Check, Server, Cpu, HardDrive, Globe, Shield, ArrowRight, Send, Database, Lock, Download, Activity, MoveRight } from 'lucide-react';
import { motion } from 'framer-motion';

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const fadeInScale = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 }
};

const fadeInScaleTransition = {
  duration: 0.5,
  ease: [0.43, 0.13, 0.23, 0.96]
};

const hostingPlans = [
  {
    name: 'Shared Hosting',
    description: 'Perfect for small to medium websites',
    icon: <Server className="w-6 h-6" />,
    href: '/hosting/shared-hosting',
    features: [
      'Unlimited Bandwidth',
      'Free SSL Certificate',
      'cPanel Control Panel',
      '24/7 Support',
    ],
  },
  {
    name: 'WordPress Hosting',
    description: 'Optimized for WordPress websites',
    icon: <Globe className="w-6 h-6" />,
    href: '/hosting/wordpress-hosting',
    features: [
      'Auto WordPress Updates',
      'WP-CLI Enabled',
      'Free Website Migration',
      'Advanced Security',
    ],
  },
  {
    name: 'VPS Hosting',
    description: 'Dedicated resources and full control',
    icon: <Cpu className="w-6 h-6" />,
    href: '/hosting/vps-hosting',
    features: [
      'Dedicated vCPU Cores',
      'Root Access',
      'SSD Storage',
      'Scalable Resources',
    ],
  },
  {
    name: 'Dedicated Server',
    description: 'Maximum performance and security',
    icon: <HardDrive className="w-6 h-6" />,
    href: '/hosting/dedicated-server',
    features: [
      'Full Server Control',
      'Enterprise Hardware',
      'DDoS Protection',
      'IPMI Access',
    ],
  },
];

export default function HostingPage() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-36 pb-36 md:pb-36 min-h-[80vh] flex items-center -mt-2">
              {/* Base gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[-1]"></div>
              
              {/* Server rack grid pattern */}
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:4px_100%]"></div>
                <div className="absolute inset-0 bg-[linear-gradient(0deg,transparent_0%,rgba(255,255,255,0.05)_50%,transparent_100%)] bg-[length:100%_4px]"></div>
              </div>
      
              {/* Animated network connections */}
              <div className="absolute inset-0">
                <div className="absolute h-px w-full top-1/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse"></div>
                <div className="absolute h-px w-full top-2/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-300"></div>
                <div className="absolute h-px w-full top-3/4 left-0 bg-gradient-to-r from-transparent via-primary-500/20 to-transparent animate-pulse delay-700"></div>
              </div>
      
              {/* Data flow lines */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute w-px h-full left-1/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow"></div>
                <div className="absolute w-px h-full left-2/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-300"></div>
                <div className="absolute w-px h-full left-3/4 top-0 bg-gradient-to-b from-transparent via-primary-500/20 to-transparent animate-network-flow delay-700"></div>
              </div>
      
              {/* Server status indicators */}
              <div className="absolute inset-0">
                <div className="absolute top-10 left-[10%] flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
                </div>
                <div className="absolute bottom-10 right-[10%] flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-100"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-ping delay-200"></div>
                </div>
              </div>
      
              {/* Server icons with glow */}
              <div className="absolute inset-0">
                <motion.div 
                  className="absolute left-[5%] top-1/4 group"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <Server className="relative text-primary-400 w-16 h-16 transform group-hover:scale-110 transition-transform" />
                </motion.div>
                <motion.div 
                  className="absolute right-[5%] bottom-1/4 group"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <HardDrive className="relative text-primary-400 w-12 h-12 transform group-hover:scale-110 transition-transform" />
                </motion.div>
                <motion.div 
                  className="absolute right-[15%] top-1/3 group"
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="absolute inset-0 bg-primary-500/20 blur-xl rounded-full transform scale-150 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <Cpu className="relative text-primary-400 w-20 h-20 transform group-hover:scale-110 transition-transform" />
                </motion.div>
              </div>
      
              {/* Binary data particles */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full">
                  {Array.from({ length: 20 }).map((_, i) => {
                    const delayClasses = ['delay-0', 'delay-100', 'delay-200', 'delay-300'];
                    const binaryValue = i % 2 === 0 ? '1' : '0';
                    return (
                      <div
                        key={i}
                        className={`absolute text-primary-500/10 text-xs animate-float-binary ${delayClasses[i % delayClasses.length]}`}
                        style={{
                          left: `${(i % 5) * 25}%`,
                          top: `${Math.floor(i / 5) * 25}%`
                        }}
                      >
                        {binaryValue}
                      </div>
                    );
                  })}
                </div>
              </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 mt-12">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Web Hosting Solutions
              </span>
            </motion.div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white">
            Find Your <span className="text-transparent bg-clip-text bg-gradient-rainbow">Perfect Hosting Plan</span>
            </h1>
            
            <p className="text-xl mb-8 text-slate-300">
              From shared hosting to dedicated servers, we have the perfect solution for your website.
            </p>
          </div>
        </div>
      </section>

      {/* Hosting Plans Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <motion.span 
              className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-4"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Hosting Server Types
            </motion.span>
            
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Hosting Plans</span> for Every Need
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Choose the perfect hosting solution for your website or application.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                name: "Shared Hosting",
                description: "Perfect for personal websites, blogs, and small businesses getting started online.",
                price: "Starting from $1.00",
                link: "/hosting/shared-hosting",
                color: "from-primary-500 to-primary-700",
                popular: true
              },
              {
                name: "WordPress Hosting",
                description: "Optimized for WordPress with enhanced performance and auto-updates.",
                price: "Starting from $1.50",
                link: "/hosting/wordpress-hosting",
                color: "from-secondary-500 to-secondary-700"
              },
              {
                name: "VPS Hosting",
                description: "Dedicated resources with full control and scalability for growing businesses.",
                price: "Starting from $5.00",
                link: "/hosting/vps-hosting",
                color: "from-tertiary-500 to-tertiary-700"
              },
              {
                name: "Dedicated Server",
                description: "Enterprise-grade servers with maximum performance for high-traffic websites.",
                price: "Starting from $49.00",
                link: "/hosting/dedicated-server",
                color: "from-accent-500 to-accent-700"
              }
            ].map((plan, index) => (
              <motion.div 
                key={plan.name}
                className={`bg-slate-800 rounded-xl shadow-xl overflow-hidden border ${plan.popular ? 'border-primary-500' : 'border-slate-700'} flex flex-col relative group`}
                variants={fadeInScale}
                transition={fadeInScaleTransition}
                whileHover={{ y: -15, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-gradient-rainbow text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                    POPULAR
                  </div>
                )}
                <div className={`p-6 bg-gradient-to-r ${plan.color} border-b border-slate-700`}>
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline">
                    <span className="text-base font-bold text-white">{plan.price}</span>
                  </div>
                  <p className="text-sm text-white/80 mt-2">{plan.description}</p>
                </div>
                <div className="p-6 flex-grow">
                  <ul className="space-y-3">
                    {(plan.name === "Shared Hosting" ? [
                      "Lightning-Fast Page Loading",
                      "Easy Website Builder",
                      "One-Click App Installer",
                      "Free Domain Transfer",
                      "99.9% Uptime Guarantee",
                      "24/7 Expert Support"
                    ] : plan.name === "WordPress Hosting" ? [
                      "WordPress Pre-installed",
                      "Auto WordPress Updates",
                      "Enhanced Security",
                      "WP Optimized Servers",
                      "Free Theme Installation",
                      "Expert WP Support"
                    ] : plan.name === "VPS Hosting" ? [
                      "Dedicated Resources",
                      "Full Root Access",
                      "Instant Scalability",
                      "SSD Storage",
                      "Cloud Infrastructure",
                      "Advanced Security"
                    ] : [
                      "Enterprise Hardware",
                      "Dedicated IP Address",
                      "Custom Configuration",
                      "Premium Support",
                      "DDoS Protection",
                      "100% Resource Control"
                    ]).map((feature, index) => (
                      <li key={`${plan.name}-feature-${index}`} className="flex items-center">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={plan.link}
                  className={`block w-full py-4 px-6 text-center font-medium text-white transition-colors duration-300 ${plan.name === 'Shared Hosting' ? 'bg-gradient-rainbow shadow-neon hover:shadow-neon-hover' : `bg-gradient-to-r ${plan.color} hover:opacity-90`}`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Why Choose <span className="text-transparent bg-clip-text bg-gradient-rainbow">1 Dollar Cloud's Hosting Services?</span>
            </h2>
            <p className="text-slate-400">
              Experience reliable, secure, and high-performance hosting solutions backed by 24/7 expert support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: '99.9% Uptime',
                description: 'We guarantee your website will be up and running when your visitors need it.',
                icon: <Server className="w-6 h-6" />,
              },
              {
                title: 'Advanced Security',
                description: 'Built-in security features to protect your website from threats and attacks.',
                icon: <Shield className="w-6 h-6" />,
              },
              {
                title: 'Expert Support',
                description: '24/7 support from our team of hosting experts to help you with any issues.',
                icon: <Globe className="w-6 h-6" />,
              },
              {
                title: 'Lightning-Fast SSD',
                description: 'Pure SSD storage for blazing-fast website loading speeds and better performance.',
                icon: <HardDrive className="w-6 h-6" />,
              },
              {
                title: 'Auto Backups',
                description: 'Daily automated backups to keep your website data safe and secure.',
                icon: <Database className="w-6 h-6" />,
              },
              {
                title: 'Free SSL Certificates',
                description: 'Secure your website with free SSL certificates to protect user data.',
                icon: <Lock className="w-6 h-6" />,
              },
              {
                title: 'One-Click Installs',
                description: 'Install popular applications and CMS platforms with a single click.',
                icon: <Download className="w-6 h-6" />,
              },
              {
                title: 'Resource Monitoring',
                description: 'Real-time monitoring of server resources and performance metrics.',
                icon: <Activity className="w-6 h-6" />,
              },
              {
                title: 'Free Migration',
                description: 'Seamless website migration from your current host to our servers.',
                icon: <MoveRight className="w-6 h-6" />,
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-900/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl group"
                whileHover={{ y: -10, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
              >
                <div className="bg-gradient-to-br from-primary-500 to-primary-100 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-6 group-hover:bg-gradient-rainbow transition-all duration-500">
                  <div className="text-primary-500 group-hover:text-white transition-colors duration-300">
                    {feature.icon}
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Infrastructure Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Enterprise-Grade Infrastructure</span>
            </h2>
            <p className="text-slate-400">
              Powered by cutting-edge technology and state-of-the-art data centers
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Global Data Centers',
                description: 'Strategically located data centers across multiple continents ensuring low latency and high availability.',
                features: [
                  'Tier-4 Data Center Facilities',
                  'Redundant Power Systems',
                  'Advanced Cooling Infrastructure',
                  'Multiple Network Carriers'
                ]
              },
              {
                title: 'Server Technology',
                description: 'Latest generation hardware with enterprise-level components for maximum performance.',
                features: [
                  'Intel Xeon Processors',
                  'NVMe SSD Storage',
                  'ECC DDR4 Memory',
                  'RAID Configuration'
                ]
              },
              {
                title: 'Network Architecture',
                description: 'Multi-layered network design with advanced DDoS protection and traffic optimization.',
                features: [
                  '100 Gbps Network',
                  'BGP Routing',
                  'Smart Traffic Analysis',
                  'IPv6 Ready'
                ]
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50"
              >
                <h3 className="text-xl font-semibold text-white mb-4">{item.title}</h3>
                <p className="text-slate-400 mb-6">{item.description}</p>
                <ul className="space-y-3">
                  {item.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-slate-300">
                      <Check className="w-5 h-5 text-primary-400 mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Management Tools Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Powerful Management Tools</span>
            </h2>
            <p className="text-slate-400">
              Take control of your hosting environment with our comprehensive suite of management tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: 'Control Panel Access',
                features: [
                  'User-friendly cPanel/Plesk interface',
                  'One-click application installer',
                  'Email account management',
                  'Database administration',
                  'File manager with FTP access',
                  'SSL certificate management'
                ]
              },
              {
                title: 'Monitoring & Analytics',
                features: [
                  'Real-time resource monitoring',
                  'Bandwidth usage statistics',
                  'Server health metrics',
                  'Traffic analytics',
                  'Error log analysis',
                  'Performance optimization tools'
                ]
              }
            ].map((tool, index) => (
              <motion.div
                key={tool.title}
                initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700/50"
              >
                <h3 className="text-2xl font-semibold text-white mb-6">{tool.title}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {tool.features.map((feature, i) => (
                    <div key={i} className="flex items-center text-slate-300">
                      <Check className="w-5 h-5 text-primary-400 mr-2 flex-shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Background elements - Data center visualization */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800"></div>
        
        {/* Server visualization background */}
        <div className="absolute inset-0">
          {/* Connection lines */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`line-${i}`}
                className="absolute bg-primary-600/10 h-[1px]"
                style={{
                  top: `${5 + i * 5}%`,
                  left: '0',
                  right: '0',
                }}
                animate={{
                  opacity: [0.1, 0.3, 0.1],
                }}
                transition={{
                  duration: 3 + (i % 5),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.1,
                }}
              />
            ))}
            
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={`vline-${i}`}
                className="absolute bg-primary-600/10 w-[1px]"
                style={{
                  left: `${5 + i * 5}%`,
                  top: '0',
                  bottom: '0',
                }}
                animate={{
                  opacity: [0.1, 0.2, 0.1],
                }}
                transition={{
                  duration: 4 + (i % 3),
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.2,
                }}
              />
            ))}
          </div>
          
          {/* Data nodes */}
          <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => {
              const size = 2 + (i % 3) * 2;
              return (
                <motion.div
                  key={`node-${i}`}
                  className="absolute rounded-full bg-primary-500/30"
                  style={{
                    width: size,
                    height: size,
                    top: `${10 + (i * 73) % 80}%`,
                    left: `${5 + (i * 57) % 90}%`,
                  }}
                  animate={{
                    opacity: [0.2, 0.5, 0.2],
                    scale: [1, 1.5, 1],
                  }}
                  transition={{
                    duration: 3 + (i % 4),
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              );
            })}
          </div>
          
          {/* Data flow animations */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => {
              const isVertical = i % 2 === 0;
              const size = isVertical ? '1px' : '2px';
              const startPosition = `${10 + (i * 37) % 80}%`;
              
              return (
                <motion.div
                  key={`flow-${i}`}
                  className="absolute bg-gradient-to-r from-primary-500/0 via-primary-500/50 to-primary-500/0"
                  style={{
                    width: isVertical ? size : '100px',
                    height: isVertical ? '50px' : size,
                    top: isVertical ? '0' : startPosition,
                    left: isVertical ? startPosition : '0',
                  }}
                  animate={{
                    [isVertical ? 'top' : 'left']: ['0%', '100%'],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 4 + (i % 3) * 2,
                    repeat: Infinity,
                    ease: "linear",
                    delay: i * 0.2,
                  }}
                />
              );
            })}
          </div>
          
          {/* Subtle grid overlay */}
          <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
          
          {/* Subtle noise texture */}
          <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-900/30 text-primary-300 text-sm font-medium mb-6">
                Get Started Today
              </span>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
                Ready to <span className="text-transparent bg-clip-text bg-gradient-rainbow">Get Started</span>?
              </h2>
              
              <p className="text-xl mb-8 text-slate-300">
                Choose the perfect hosting plan for your WordPress site and get online today.
              </p>
              
              <motion.div
                className="inline-block"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/hosting"
                  className="bg-gradient-rainbow text-white px-8 py-4 rounded-lg font-medium text-center transition-all shadow-neon inline-flex items-center justify-center"
                >
                  Get Started Now <ArrowRight className="ml-2" />
                </Link>
              </motion.div>
              
              <motion.div
                className="mt-12 max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h4 className="text-xl font-semibold text-white mb-4">Subscribe to our newsletter</h4>
                <form onSubmit={async (e) => {
                   e.preventDefault();
                   const form = e.target as HTMLFormElement;
                   const email = (form.elements.namedItem('email') as HTMLInputElement).value;
                   let message = '';
                   let color = '';
                   if (email) {
                     const result = await subscribeNewsletter(email);
                     if (result.ok) {
                       message = result.message;
                        color = 'green';
                        (form.elements.namedItem('email') as HTMLInputElement).value = ''; // Clear the input field
                      } else {
                        message = result.message;
                        color = 'red';
                      }
                    } else {
                      message = 'Please enter your email address.';
                      color = 'red';
                    }
                    const subscribeMessage = document.getElementById('subscribe-message');
                    if (subscribeMessage) {
                      subscribeMessage.textContent = message;
                      subscribeMessage.style.color = color;
                    }
                  }}>
                   <div className="flex flex-col sm:flex-row">
                     <input
                       type="email"
                      name="email"
                      placeholder="Your email"
                      className="bg-slate-800/80 text-white rounded-l-lg sm:rounded-r-none rounded-r-lg sm:rounded-b-none rounded-b-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 w-full border border-slate-700 mb-2 sm:mb-0"
                    />
                    <motion.button
                      type="submit"
                      className="bg-gradient-rainbow text-white px-6 py-3 rounded-l-lg sm:rounded-l-none rounded-r-lg font-medium flex items-center justify-center"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Send className="mr-2" />
                      <span>Subscribe</span>
                    </motion.button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    By subscribing, you agree to our Privacy Policy and consent to receive updates.
                  </p>
                  <p id="subscribe-message" className="mt-2 text-base text-center"></p>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}